import type { SessionEvent } from "../core/types"
import { currentPath } from "./collectors"

export interface VitalMetric {
  name: "LCP" | "INP" | "CLS" | "TTFB" | "FCP" | "FID"
  value: number
  rating: "good" | "needs-improvement" | "poor"
  unit: "ms" | "score"
  delta?: number
}

function getRating(name: string, value: number): "good" | "needs-improvement" | "poor" {
  switch (name) {
    case "LCP":
      return value <= 2500 ? "good" : value <= 4000 ? "needs-improvement" : "poor"
    case "INP":
    case "FID":
      return value <= 200 ? "good" : value <= 500 ? "needs-improvement" : "poor"
    case "CLS":
      return value <= 0.1 ? "good" : value <= 0.25 ? "needs-improvement" : "poor"
    case "TTFB":
      return value <= 800 ? "good" : value <= 1800 ? "needs-improvement" : "poor"
    case "FCP":
      return value <= 1800 ? "good" : value <= 3000 ? "needs-improvement" : "poor"
    default:
      return "good"
  }
}

export function initVitalsObserver(onVital: (ev: SessionEvent) => void): () => void {
  if (typeof window === "undefined" || typeof PerformanceObserver === "undefined") {
    return () => {}
  }

  const observers: PerformanceObserver[] = []
  let clsValue = 0

  const emit = (name: VitalMetric["name"], value: number, unit: "ms" | "score" = "ms") => {
    const rounded = unit === "score" ? Math.round(value * 1000) / 1000 : Math.round(value)
    const rating = getRating(name, rounded)
    onVital({
      type: "vital",
      ts: new Date().toISOString(),
      target: name,
      path: currentPath(),
      text: `${name}: ${rounded}${unit === "ms" ? "ms" : ""}`,
      meta: {
        metric: name,
        value: rounded,
        unit,
        rating,
      },
    })
  }

  // 1. Full Page Load & TTFB (Navigation Timing)
  const captureNavigationTiming = () => {
    try {
      const navEntries = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[]
      if (navEntries && navEntries.length > 0) {
        const nav = navEntries[0]
        const ttfb = Math.max(0, nav.responseStart - nav.requestStart)
        if (ttfb > 0) emit("TTFB", ttfb, "ms")

        const loadDuration = nav.loadEventEnd > 0 ? Math.round(nav.loadEventEnd - nav.startTime) : Math.round(performance.now())
        const isForeground = typeof document !== "undefined" ? document.visibilityState === "visible" : true
        if (loadDuration > 0) {
          onVital({
            type: "pageload",
            ts: new Date().toISOString(),
            target: "window",
            path: currentPath(),
            text: `Page loaded in ${loadDuration}ms`,
            meta: {
              duration_ms: loadDuration,
              ttfb,
              dom_interactive: Math.round(nav.domInteractive - nav.startTime),
              dom_complete: Math.round(nav.domComplete - nav.startTime),
              is_foreground: isForeground,
            },
          })
        }
      }
    } catch {}
  }

  if (document.readyState === "complete") {
    setTimeout(captureNavigationTiming, 100)
  } else {
    window.addEventListener("load", () => setTimeout(captureNavigationTiming, 100), { once: true })
  }

  // 2. FCP (Paint Timing)
  try {
    const paintObserver = new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries()) {
        if (entry.name === "first-contentful-paint") {
          emit("FCP", entry.startTime, "ms")
        }
      }
    })
    paintObserver.observe({ type: "paint", buffered: true })
    observers.push(paintObserver)
  } catch {}

  // 3. LCP (Largest Contentful Paint)
  try {
    const lcpObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries()
      const lastEntry = entries[entries.length - 1]
      if (lastEntry) {
        emit("LCP", lastEntry.startTime, "ms")
      }
    })
    lcpObserver.observe({ type: "largest-contentful-paint", buffered: true })
    observers.push(lcpObserver)
  } catch {}

  // 4. CLS (Cumulative Layout Shift)
  try {
    const clsObserver = new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries() as any[]) {
        if (!entry.hadRecentInput) {
          clsValue += entry.value || 0
          emit("CLS", clsValue, "score")
        }
      }
    })
    clsObserver.observe({ type: "layout-shift", buffered: true })
    observers.push(clsObserver)
  } catch {}

  // 5. INP / FID (Interaction Responsiveness)
  try {
    const inpObserver = new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries() as any[]) {
        const duration = entry.duration || entry.processingEnd - entry.startTime || 0
        if (duration > 0) {
          emit("INP", duration, "ms")
        }
      }
    })
    inpObserver.observe({ type: "first-input", buffered: true })
    observers.push(inpObserver)
  } catch {}

  return () => {
    for (const obs of observers) {
      try {
        obs.disconnect()
      } catch {}
    }
  }
}
